import React, { useEffect, useState, useRef } from 'react';
import { Box } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Col, Form, Modal } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

const ModalCalendar = ({
  show,
  handleClose,
  events,
  allLocations,
  personnel,
  instrumentSettingsEventStatus,
  instrumentSettingsEventType,
  instruments
}) => {
  const history = useHistory();
  const calendarRef = useRef(null);
  const [filteredEvents, setFilteredEvents] = useState(events);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedResponsible, setSelectedResponsible] = useState('');
  const StyledCalendar = styled('div')({
    width: 'calc(100% + 2px)',
    marginLeft: -1,
    textDecoration: 'none',

    '& .fc': {
      '--fc-today-bg-color': '#42424214',
      color: 'black'
    },
    '& .fc .fc-button': {
      backgroundColor: '#3788D8',
      textTransform: 'capitalize',
      border: '1px solid #3788D8',
      cursor: 'pointer',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#2A6CAD',
        borderColor: '#2A6CAD'
      },
      '&:active': {
        backgroundColor: '#2A6CAD',
        borderColor: '#2A6CAD'
      }
    },
    '& .fc .fc-col-header-cell-cushion': {
      textDecoration: 'none',
      color: 'black',
      fontWeight: 500
    },

    '& .fc .fc-daygrid-day-number': {
      textDecoration: 'none',
      color: 'black',
      fontWeight: 500
    },

    '& .fc .fc-timegrid-axis, & .fc .fc-timegrid-slot-label, & .fc .fc-timegrid-col-header-cell-cushion':
      {
        textDecoration: 'none',
        color: 'black'
      },

    '& .fc .fc-toolbar-title': {
      textDecoration: 'none',
      color: 'black',
      fontWeight: 400,
      fontSize: '1.2rem'
    },

    '& .fc-event-title': {
      whiteSpace: 'pre-wrap'
    },

    '& .fc-event': {
      cursor: 'pointer',
      borderRadius: '5px',
      margin: '1px',
      justifyContent: 'center',
      textAlign: 'center',
      fontSize: '1rem',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2
    },

    '& .fc-daygrid-event > div': {
      textDecoration: 'none',
      width: '100% !important'
    },
    '& .fc .fc-more-link': {
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
      height: '100% !important',
      width: '100% !important',
      color: 'black !important',
      backgroundColor: 'white !important',
      textDecoration: 'none !important',
      fontSize: '1.2em',
      marginTop: '4px',
      textAlign: 'center !important'
    },
    '& .fc .fc-tooltip': {
      minWidth: '3500px',
      whiteSpace: 'normal',
      fontSize: '14px',
      padding: '10px',
      textAlign: 'left'
    },
    '& .fc .fc-toolbar.fc-header-toolbar': {
      marginBottom: '0.2em !important'
    }
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const monthNames = Array.from({ length: 12 }, (_, i) => ({
    label: new Date(2000, i).toLocaleString('default', { month: 'long' }),
    value: i
  }));

  const handleMonthChange = (event) => {
    const month = event.target.value;
    setSelectedMonth(month);
    goToDate(selectedYear, month);
  };
  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    goToDate(year, selectedMonth);
  };
  const goToDate = (year, month) => {
    const calendarApi = calendarRef.current.getApi();
    if (calendarApi) {
      const date = new Date(year, month, 1);
      calendarApi.gotoDate(date);
    }
  };

  const transformEvents = (events) =>
    events.map((event) => {
      const hasValidDate = event.dateDue && typeof event.dateDue.seconds === 'number';
      const formattedDate = hasValidDate
        ? new Date(event.dateDue.seconds * 1000).toISOString().split('T')[0]
        : null;

      return {
        id: event.id,
        title: `${event.instrumentName}`,
        date: formattedDate,
        extendedProps: {
          eventNumber: event.eventNumber,
          location: event.location,
          instrument: event.instrumentName,
          status: event.statusName,
          eventType: event.eventType,
          responsibleParty: event.responsibleParty
        }
      };
    });

  const renderEventContent = (eventInfo) => {
    const { status, instrument, location, eventType, responsibleParty, eventNumber } =
      eventInfo.event.extendedProps;

    return (
      <Tooltip
        title={
          <Box sx={{ minWidth: '500px', p: 1 }}>
            <Typography variant="body1">
              <strong>ID:</strong> {eventNumber}
            </Typography>
            <Typography variant="body1">
              <strong>Location:</strong> {location}
            </Typography>
            <Typography variant="body1">
              <strong>Instrument:</strong> {instrument}
            </Typography>
            <Typography variant="body1">
              <strong>Status:</strong> {status}
            </Typography>
            <Typography variant="body1">
              <strong>Event Type:</strong> {eventType}
            </Typography>
            <Typography variant="body1">
              <strong>Responsible Party:</strong> {responsibleParty}
            </Typography>
          </Box>
        }
        placement="right"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: '#fff',
              color: '#000',
              border: '1px solid #ccc',
              boxShadow: 2,
              fontSize: '1em',
              maxWidth: 400,
              whiteSpace: 'normal',
              padding: '5px'
            }
          }
        }}
      >
        <div>{eventInfo.event.title}</div>
      </Tooltip>
    );
  };

  const handleEventClick = (info) => {
    const eventId = info.event.id;
    history.push(`/instrument/events/${eventId}`);
  };

  useEffect(() => {
    //Enrich event data
    const enrichedEvents = events.map((row) => {
      const dataInstrument = instruments.find((item) => item.id === row.instrumentName);
      const dataStatus = instrumentSettingsEventStatus.find((item) => item.id === row.status);
      const eventType = instrumentSettingsEventType.find((item) => item.id === row.eventType);
      const location = allLocations.find((item) => item.id === dataInstrument?.location);
      const responsibleParty = personnel.find((item) => item.id === row.responsibleParty);
      return {
        ...row,
        location: location?.name,
        instrumentId: dataInstrument?.id,
        instrumentName: dataInstrument?.name,
        status: dataStatus?.name,
        eventType: eventType?.name,
        responsibleParty: responsibleParty?.name
      };
    });
    //Filter data
    let filtered = enrichedEvents;
    if (selectedLocation) {
      filtered = filtered.filter((e) => e.location === selectedLocation);
    }
    if (selectedResponsible) {
      filtered = filtered.filter((e) => e.responsibleParty === selectedResponsible);
    }

    // Set result
    setFilteredEvents(filtered);
  }, [
    selectedLocation,
    selectedResponsible,
    events,
    instruments,
    instrumentSettingsEventStatus,
    instrumentSettingsEventType,
    allLocations,
    personnel
  ]);

  return (
    <Modal show={show} onHide={handleClose} dialogClassName="wide-modal" centered>
      <Modal.Header closeButton>
        <Modal.Title>Calendar View</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Form.Group as={Col}>
              <Form.Label>Month</Form.Label>
              <Form.Select value={selectedMonth} onChange={handleMonthChange}>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i}>
                    {monthNames[i].label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group as={Col}>
              <Form.Label>Year</Form.Label>
              <Form.Select value={selectedYear} onChange={handleYearChange}>
                {Array.from({ length: 11 }, (_, i) => {
                  const year = new Date().getFullYear() - 5 + i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Box>
          <Form.Group as={Col} controlId="location">
            <Form.Label>Location</Form.Label>
            <Form.Select onChange={(event) => setSelectedLocation(event.target.value)}>
              <option value="">All locations</option>
              {allLocations.map((option) => (
                <option key={option.id} value={option.name}>
                  {option.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>Responsible Party</Form.Label>
            <Form.Select onChange={(event) => setSelectedResponsible(event.target.value)}>
              <option value="">All Responsible Party</option>
              {personnel?.map((option) => (
                <option key={option.id} value={option.name}>
                  {option.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Box>

        <StyledCalendar>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={transformEvents(filteredEvents)}
            eventClick={handleEventClick}
            height="auto"
            dayMaxEvents={1}
            eventContent={renderEventContent}
            ref={calendarRef}
            initialDate={new Date(selectedYear, selectedMonth, 1)}
            datesSet={(dateInfo) => {
              setSelectedMonth(dateInfo.view.currentStart.getMonth());
              setSelectedYear(dateInfo.view.currentStart.getFullYear());
            }}
          />
        </StyledCalendar>
      </Modal.Body>
    </Modal>
  );
};

export default ModalCalendar;
